import { AddMedicationDialog } from '@/components/molecules/AddMedicationDialog';
import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { MedicationsList } from '@liza/ui/components/records/MedicationsList';
import {
  defaultFilters,
  RecordsListControls,
  type RecordsListFilters,
} from '@liza/ui/components/records/RecordsListControls';
import { useRecordPromotionStatus } from '@liza/ui/hooks/useRecordPromotionStatus';
import {
  getMedicationReferenceIdsWithoutDisplay,
  hydrateMedicationReferenceDisplays,
} from '@liza/ui/utils/medicationDisplay';
import { escapeFilterString, groupMedicationRecords } from '@liza/fhir-utils';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedId?: string;
  selectedResourceType?: 'MedicationRequest' | 'MedicationStatement';
};

const PAGE_SIZE = 50;

export const Medications = ({ onViewChange, patient, selectedId, selectedResourceType }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);

  const escapedQuery = escapeFilterString(filters.searchQuery);

  // A medication row is either a Liza-authored MedicationRequest or an ingested MedicationStatement.
  // Each is paginated independently, then merged for display — so both queries advance at the same
  // offset and "load more" pages whichever still has results. Their list keys are preserved (not a
  // composite key) so the existing per-resource create invalidations keep refreshing this view.
  const requestOptions = {
    ownership: filters.ownership,
    status: filters.status,
    searchParams: escapedQuery
      ? { _filter: `(code co "${escapedQuery}" or medication.code co "${escapedQuery}")` }
      : undefined,
  };
  const statementOptions = {
    ownership: filters.ownership,
    status: filters.status,
    searchParams: escapedQuery ? { _filter: `code co "${escapedQuery}"` } : undefined,
  };

  const medicationRequestsQuery = useInfiniteQuery({
    queryKey: trpc.patient.medicationRequest.list.queryKey({
      medplumPatientId: patient.medplumId,
      options: requestOptions,
    }),
    queryFn: async ({ pageParam }) =>
      trpcClient.patient.medicationRequest.list.query({
        medplumPatientId: patient.medplumId,
        options: requestOptions,
        offset: pageParam,
        count: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastOffset) => (lastPage.hasMore ? lastOffset + PAGE_SIZE : undefined),
  });

  const medicationStatementsQuery = useInfiniteQuery({
    queryKey: trpc.patient.medicationStatement.list.queryKey({
      medplumPatientId: patient.medplumId,
      options: statementOptions,
    }),
    queryFn: async ({ pageParam }) =>
      trpcClient.patient.medicationStatement.list.query({
        medplumPatientId: patient.medplumId,
        options: statementOptions,
        offset: pageParam,
        count: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastOffset) => (lastPage.hasMore ? lastOffset + PAGE_SIZE : undefined),
  });

  const medicationRequests = useMemo(
    () => medicationRequestsQuery.data?.pages.flatMap((page) => page.medicationRequests) ?? [],
    [medicationRequestsQuery.data],
  );
  const medicationStatements = useMemo(
    () => medicationStatementsQuery.data?.pages.flatMap((page) => page.medicationStatements) ?? [],
    [medicationStatementsQuery.data],
  );

  // Drive paging off the sentinel's `inView` state, re-running as each list settles (`data` deps,
  // mirroring `useInfiniteRecordList`), so grouping collapsing a page to few rows can't strand the
  // sentinel in view. Depend on the specific fields rather than the whole query objects to avoid
  // firing on unrelated query-state churn.
  const { ref: bottomSentinelRef, inView } = useInView({ rootMargin: '200px' });
  const {
    hasNextPage: reqHasNextPage,
    isFetchingNextPage: reqIsFetchingNextPage,
    fetchNextPage: fetchNextRequests,
    data: reqData,
  } = medicationRequestsQuery;
  const {
    hasNextPage: stmtHasNextPage,
    isFetchingNextPage: stmtIsFetchingNextPage,
    fetchNextPage: fetchNextStatements,
    data: stmtData,
  } = medicationStatementsQuery;
  useEffect(() => {
    if (!inView) return;
    if (reqHasNextPage && !reqIsFetchingNextPage) void fetchNextRequests();
    if (stmtHasNextPage && !stmtIsFetchingNextPage) void fetchNextStatements();
  }, [
    inView,
    reqHasNextPage,
    reqIsFetchingNextPage,
    fetchNextRequests,
    reqData,
    stmtHasNextPage,
    stmtIsFetchingNextPage,
    fetchNextStatements,
    stmtData,
  ]);

  // Grouped rather than flattened for display: the group sizes drive the row's "Nx" badge, and the
  // detail panel re-derives the same groups (see `useMedicationGroup`) to list the collapsed records.
  const medicationGroups = useMemo(() => {
    if (!medicationRequestsQuery.data || !medicationStatementsQuery.data) {
      return undefined;
    }
    return groupMedicationRecords([...medicationRequests, ...medicationStatements]);
  }, [medicationRequestsQuery.data, medicationStatementsQuery.data, medicationRequests, medicationStatements]);

  const mergedMedications = useMemo(() => medicationGroups?.map((group) => group.record), [medicationGroups]);

  const groupSizeById = useMemo(
    () => new Map(medicationGroups?.map((group) => [group.record.id, group.superseded.length + 1]) ?? []),
    [medicationGroups],
  );

  const medicationReferenceIds = useMemo(
    () => (mergedMedications ? getMedicationReferenceIdsWithoutDisplay(mergedMedications) : []),
    [mergedMedications],
  );
  const medicationsLookupQuery = useQuery(
    trpc.patient.medication.batchGet.queryOptions(
      { ids: medicationReferenceIds },
      { enabled: medicationReferenceIds.length > 0 },
    ),
  );
  const hydratedMedications = useMemo(
    () =>
      mergedMedications
        ? hydrateMedicationReferenceDisplays(mergedMedications, medicationsLookupQuery.data?.medications ?? [])
        : undefined,
    [mergedMedications, medicationsLookupQuery.data?.medications],
  );

  const { mutateAsync: createMedicationRequest } = useMutation(
    trpc.patient.medicationRequest.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.medicationRequest.list.queryKey({ medplumPatientId: patient.medplumId }),
        });
      },
    }),
  );

  const { promote, getPromotionStatus } = useRecordPromotionStatus();

  const handlePromotionClick = useCallback(
    (id: string): void => {
      const source = medicationRequests.find((m) => m.id === id);
      if (!source) return;

      void promote(id, async () =>
        createMedicationRequest({
          resource: {
            ...source,
            id: undefined,
            subject: { reference: `Patient/${patient.medplumId}` },
            authoredOn: new Date().toISOString(),
          },
        }),
      );
    },
    [createMedicationRequest, medicationRequests, patient, promote],
  );

  const isLoading = medicationRequestsQuery.isLoading || medicationStatementsQuery.isLoading;
  const isError = medicationRequestsQuery.isError || medicationStatementsQuery.isError;
  const error = medicationRequestsQuery.error ?? medicationStatementsQuery.error;
  const isFetchingNextPage = medicationRequestsQuery.isFetchingNextPage || medicationStatementsQuery.isFetchingNextPage;

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('medications')}
        searchPlaceholder={t('searchMedications')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addMedication')}
      />
      <AddMedicationDialog
        medplumPatientId={patient.medplumId}
        organizationId={patient.organizationId}
        isOpen={isAddOpen}
        onOpenChange={setIsAddOpen}
      />
      <RecordListScroll
        isLoading={isLoading}
        isError={isError}
        error={error}
        isFetchingNextPage={isFetchingNextPage}
        bottomSentinelRef={bottomSentinelRef}
      >
        <MedicationsList
          medications={hydratedMedications ?? []}
          groupSizeById={groupSizeById}
          onSelectMedication={(id, resourceType) => onViewChange({ type: 'medication-detail', id, resourceType })}
          selectedMedicationId={selectedId}
          selectedMedicationResourceType={selectedResourceType}
          onPromotionClick={filters.ownership === 'external' ? handlePromotionClick : undefined}
          promotionEligible={(m) => m.resourceType === 'MedicationRequest'}
          getPromotionStatus={getPromotionStatus}
        />
      </RecordListScroll>
    </div>
  );
};
